import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals, SemanticError, SemanticInfo, SemanticSuccess, SemanticWarning, Warning } from '../../color-palette/colors';
import type { PaletteRangeSemantic } from '../../color-palette/colors.interface';
import { IconButton } from '../Buttons/IconButton';

export type AlertSeverity = 'error' | 'warning' | 'success' | 'info';

export interface InlineAlertProps {
  severity?: AlertSeverity;
  title?: React.ReactNode;
  message?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  sx?: SxProps<Theme>;
  role?: 'alert' | 'status';
  id?: string;
  tabIndex?: number;
}

interface SeverityLook {
  readonly tint: PaletteRangeSemantic;
  readonly ink: string;
  readonly iconColor: string;
  readonly Icon: typeof InfoOutlinedIcon;
}

const SEVERITY: Record<AlertSeverity, SeverityLook> = {
  error: { tint: SemanticError, ink: SemanticError[900], iconColor: SemanticError[600], Icon: ErrorOutlineIcon },
  warning: { tint: SemanticWarning, ink: Warning[800], iconColor: Warning[800], Icon: WarningAmberIcon },
  success: { tint: SemanticSuccess, ink: SemanticSuccess[900], iconColor: SemanticSuccess[600], Icon: CheckCircleOutlineIcon },
  info: { tint: SemanticInfo, ink: SemanticInfo[900], iconColor: SemanticInfo[600], Icon: InfoOutlinedIcon },
};

const LINE_HEIGHT = 1.5;

const rootSx = (tint: PaletteRangeSemantic): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 2,
  p: 3,
  borderRadius: 1.5,
  bgcolor: tint[50],
  border: `1px solid ${tint[100]}`,
  color: Neutrals[700],
});

const iconSx =
  (color: string, firstLine: 'highlight' | 'label') =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    color,
    blockSize: `calc(${theme.typography[firstLine].fontSize} * ${LINE_HEIGHT})`,
  });

const mainSx: SystemStyleObject<Theme> = {
  flex: 1,
  minInlineSize: 0,
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { xs: 'flex-start', md: 'center' },
  gap: 2,
};
const textSx: SystemStyleObject<Theme> = { flex: 1, minInlineSize: 0, alignSelf: 'stretch' };
const actionSx: SystemStyleObject<Theme> = { flexShrink: 0 };
const messageSx: SystemStyleObject<Theme> = { color: Neutrals[700], lineHeight: LINE_HEIGHT };
const closeSx: SystemStyleObject<Theme> = { marginBlock: -2, marginInlineEnd: -2, color: Neutrals[700] };

export const InlineAlert = React.forwardRef<HTMLDivElement, InlineAlertProps>(
  ({ severity = 'info', title, message, icon, children, action, onClose, closeLabel, sx, role, id, tabIndex }, ref) => {
    const { t } = useTranslation();
    const { tint, ink, iconColor, Icon } = SEVERITY[severity];
    return (
      <Box ref={ref} role={role} id={id} tabIndex={tabIndex} sx={[rootSx(tint), ...(Array.isArray(sx) ? sx : [sx])]}>
        {icon !== null && <Box sx={iconSx(iconColor, title ? 'highlight' : 'label')}>{icon ?? <Icon fontSize="small" />}</Box>}
        <Box sx={mainSx}>
          <Box sx={textSx}>
            {title && (
              <Typography variant="highlight" component="p" sx={{ color: ink, lineHeight: LINE_HEIGHT }}>
                {title}
              </Typography>
            )}
            {message && (
              <Typography variant="label" component="p" sx={messageSx}>
                {message}
              </Typography>
            )}
            {children}
          </Box>
          {action && <Box sx={actionSx}>{action}</Box>}
        </Box>
        {onClose && (
          <IconButton label={closeLabel ?? t('coreComponents.inlineAlert.dismiss', 'Dismiss')} onClick={onClose} sx={closeSx}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  },
);

InlineAlert.displayName = 'InlineAlert';
